<template>
	<section class="whats-next" :style="sectionVars">
		<div class="container">
			<h2 v-if="title" class="whats-next__title headline-l">{{ title }}</h2>

			<div class="whats-next__stage">
				<div
					v-if="slides.length > 1"
					class="whats-next__arrow whats-next__arrow--prev"
					@click="triggerPrev"
				>
					<span class="whats-next__chevron" />
				</div>

				<carousel
					ref="carouselRef"
					class="whats-next__carousel"
					:items="1"
					:nav="true"
					:dots="false"
					:loop="true"
					:autoplay="false"
					:mouseDrag="true"
					:touchDrag="true"
				>
					<div v-for="(slide, i) in slides" :key="i" class="whats-next__card">
						<div class="whats-next__tabs">
							<span class="whats-next__tab">{{ slide.period }}</span>
							<span
								class="whats-next__tab is-status"
								:class="slide.statusHighlighted ? 'is-current' : 'is-planned'"
								>{{ slide.status }}</span
							>
						</div>

						<div class="whats-next__panel">
							<h3 class="whats-next__slide-title headline-l">
								{{ slide.title }}
							</h3>
							<ul class="whats-next__list">
								<li v-for="(entry, e) in slide.entries" :key="e">
									<span class="whats-next__check" />
									<span>{{ entry }}</span>
								</li>
							</ul>
						</div>
					</div>
				</carousel>

				<div
					v-if="slides.length > 1"
					class="whats-next__arrow whats-next__arrow--next"
					@click="triggerNext"
				>
					<span class="whats-next__chevron" />
				</div>
			</div>
		</div>
	</section>
</template>

<script>
// same slider the home page product carousel uses, so the paging animates
import carousel from 'vue-owl-carousel';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel';

export default {
	name: 'WhatsNextSection',
	props: {
		title: { type: String, default: '' },
		// [{ period, status, title, entries: [], statusHighlighted }]
		slides: { type: Array, default: () => [] },
		prevLabel: { type: String, default: 'Previous' },
		nextLabel: { type: String, default: 'Next' },
		backgroundColor: { type: String, default: '#040428' },
		backgroundImage: { type: String, default: '' },
		borderColor: { type: String, default: '#4656FF' },
		accentColor: { type: String, default: '#01DBA8' },
		textColor: { type: String, default: '#FFFFFF' },
	},
	components: {
		carousel,
	},
	data: () => ({}),
	created() {},
	computed: {
		sectionVars() {
			return {
				'--bg': this.backgroundColor,
				'--bg-image': this.backgroundImage
					? `url(${this.backgroundImage})`
					: 'none',
				'--border': this.borderColor,
				'--accent': this.accentColor,
				'--text': this.textColor,
			};
		},
	},
	methods: {
		// owl renders its own nav; these buttons just drive it, as HomeProducts does
		triggerPrev() {
			const nav = this.$refs.carouselRef.$el.querySelector('.owl-prev');
			if (nav) nav.click();
		},
		triggerNext() {
			const nav = this.$refs.carouselRef.$el.querySelector('.owl-next');
			if (nav) nav.click();
		},
	},
};
</script>

<style lang="scss" scoped>
.whats-next {
	position: relative;
	background-color: var(--bg);
	padding: 90px 0;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		background-image: var(--bg-image);
		background-repeat: no-repeat;
		background-position: center;
		background-size: cover;
		pointer-events: none;
	}

	.container {
		position: relative;
		z-index: 1;
	}

	@media (max-width: $breakpoint-sm) {
		padding: 50px 0;
	}
}

.whats-next__title {
	color: var(--text);
	text-transform: uppercase;
	text-align: center;
	margin-bottom: 50px;
}

.whats-next__stage {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 110px;

	@media (max-width: $breakpoint-md) {
		gap: 40px;
	}

	@media (max-width: $breakpoint-sm) {
		gap: 8px;
	}
}

.whats-next__carousel {
	width: 100%;
	max-width: 560px;
	// a flex item will not shrink below its content, and owl's track is as wide
	// as every slide put together -- without this the arrows are pushed off screen
	min-width: 0;

	// owl draws its own arrows; ours sit outside the track
	&::v-deep .owl-nav {
		display: none;
	}
}

.whats-next__tabs {
	display: grid;
	grid-template-columns: 1fr 1fr;
}

.whats-next__tab {
	border: 1px solid var(--border);
	padding: 12px 16px;
	text-align: center;
	color: var(--text);
	font-size: $text-m;
	line-height: 20px;

	// the quarter that is under way is picked out; later ones stay pale
	&.is-status.is-current {
		background: var(--border);
	}

	&.is-status.is-planned {
		background: #e8e8ff;
		color: #050535;
	}
}

.whats-next__panel {
	border: 1px solid var(--border);
	border-top: 0;
	padding: 30px;
	text-align: left;

	@media (max-width: $breakpoint-sm) {
		padding: 20px;
	}
}

.whats-next__slide-title {
	color: var(--text);
	text-transform: uppercase;
	margin-bottom: 24px;
}

.whats-next__list {
	list-style: none;
	margin: 0;
	padding: 0;

	li {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		color: var(--text);
		font-size: $text-m;
		line-height: 24px;
		margin-bottom: 12px;

		&:last-child {
			margin-bottom: 0;
		}
	}
}

.whats-next__check {
	width: 24px;
	height: 24px;
	flex-shrink: 0;
	background: var(--accent);
	mask-image: url('/images/icons/check_green.svg');
	mask-repeat: no-repeat;
	mask-size: contain;
	mask-position: center;
}

.whats-next__arrow {
	padding: 10px;
	cursor: pointer;
	flex-shrink: 0;

	@media (max-width: $breakpoint-sm) {
		padding: 6px 0;
	}
}

.whats-next__chevron {
	display: block;
	width: 14px;
	height: 22px;
	background: var(--accent);
	mask-image: url('/images/icons/bb-icon_arrow-right.svg');
	mask-repeat: no-repeat;
	mask-size: contain;
	mask-position: center;

	.whats-next__arrow--prev & {
		transform: rotate(180deg);
	}
}
</style>
